import { useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { instance } from '~/utils/api/api';
import { useToastError } from '~/utils/useToastError';
import dayjs from 'dayjs';
import {
  AnnouncementForm,
  AnnouncementItem,
  AnnouncementValues,
} from '~/components/AnnouncementForm';
import Heading from '~/components/Shared/Heading/Heading';
import Page from '~/components/Shared/Layout/Page/Page';
import styles from './EditAnnouncement.module.scss';
import { useToastSuccess } from '~/utils/useToastSuccess';
import { useTranslation } from 'react-i18next';
import { getPaginate } from '~/utils/getPaginate';

const EditAnnouncement = (): JSX.Element => {
  const { announcementID } = useParams();
  const toastError = useToastError();
  const { t } = useTranslation();
  const toastSuccess = useToastSuccess();
  const { value, paginate } = getPaginate();

  const {
    data: announcement,
    isLoading,
    isFetching,
  } = useQuery<AnnouncementItem>({
    queryFn: async () => {
      const response = await instance.get(
        `admin/announcement/${announcementID}`
      );
      return response.data;
    },
    queryKey: ['announcement', announcementID],
    onError: error => {
      toastError(error);
    },
  });

  const navigate = useNavigate();

  const client = useQueryClient();

  const { mutateAsync: editAnnouncement } = useMutation(
    async ({
      module,
      status_id,
      announcement_accommodations,
      name,
      text,
      start_date,
      end_date,
      photos,
      deletedPhotoIds,
      announcement_organizations,
      tags,
      organization_all,
      tag_all,
    }: AnnouncementValues) => {
      const organization_ids = announcement_organizations.map(
        organization => organization.id
      );
      const tag_ids = tags.map(item => item.id);
      const response = await instance.put(
        `admin/announcement/${announcementID}`,
        {
          module,
          status_id,
          name,
          text,
          start_date: dayjs(start_date)
            .hour(0)
            .minute(0)
            .second(1)
            .format('YYYY-MM-DD HH:mm:ss'),
          end_date: dayjs(end_date).endOf('day').format('YYYY-MM-DD HH:mm:ss'),
          image_ids: photos.map(photo => photo.id),
          deletedPhotoIds,
          accommodation_ids: announcement_accommodations.map(
            organization => organization.id
          ),
          ...(organization_all ? {} : { organization_ids }),
          ...(tag_all ? {} : { tag_ids }),
          ...(organization_all ? { organization_all } : {}),
          ...(tag_all ? { tag_all } : {}),
        }
      );
      return response.data;
    },
    {
      onSuccess: () => {
        toastSuccess(t('news_edited'));
        client.invalidateQueries(['accommodations']);
        navigate(`/announcements?${value}=${paginate}`);
      },
      onError: error => {
        toastError(error);
      },
    }
  );

  return (
    <Page
      heading={
        <Heading
          text={announcement?.name}
          classNameText={styles.editAnnouncementText}
        />
      }
    >
      <AnnouncementForm
        isLoading={isLoading || isFetching}
        announcement={announcement}
        onSubmit={values => editAnnouncement(values)}
      />
    </Page>
  );
};

export default EditAnnouncement;
